import React, { useState } from "react";
import { api } from "@/lib/api";
import { CLIMA_LABELS, TREINO_MODULOS } from "@/lib/format";
import { CheckCircle2, X } from "lucide-react";

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            {children}
        </div>
    );
}

// Seleção múltipla de módulos de treinamento (M18)
export function ModulosCheckboxes({ value, onChange, testidPrefix = "modulo" }) {
    const toggle = (m) => {
        const set = new Set(value);
        set.has(m) ? set.delete(m) : set.add(m);
        onChange(Array.from(set));
    };
    return (
        <div className="grid grid-cols-2 gap-1.5" data-testid={`${testidPrefix}-list`}>
            {TREINO_MODULOS.map(m => (
                <label key={m} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={value.includes(m)} onChange={() => toggle(m)}
                        data-testid={`${testidPrefix}-${m}`}
                        className="h-4 w-4 rounded border-slate-300 text-[#0F2044] focus:ring-[#0F2044]" />
                    {m}
                </label>
            ))}
        </div>
    );
}

// Concluir reunião (jornada ou agenda do dashboard) → grava em Reuniões e Clima
export function CompleteMeetingTaskModal({ contract, task, onClose, onSaved }) {
    const [form, setForm] = useState({
        data: task.override_date || task.predicted_date || new Date().toISOString().slice(0, 10),
        tipo: "Acompanhamento",
        participantes: "",
        clima: "bom",
        notas: "",
        proximos_passos: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit() {
        if (!form.data) { setError("Informe a data da reunião."); return; }
        setSaving(true); setError("");
        try {
            await api.post(`/contracts/${contract.id}/meetings`, form);
            await api.patch(`/tasks/${task.id}`, {
                status: "concluido",
                observacao: `Reunião realizada em ${form.data}${form.notas ? ` — ${form.notas.slice(0, 140)}` : ""}`,
            });
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao registrar a reunião.");
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 px-4 overflow-y-auto py-6" data-testid="complete-meeting-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in my-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0F2044] font-semibold">Concluir reunião</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{task.descricao}</h3>
                        <div className="text-xs text-slate-500 mt-1">{contract.organizacao_nome} · o registro entra na aba "Reuniões e Clima" (follow-up e ações inclusos).</div>
                    </div>
                    <button onClick={onClose} data-testid="complete-meeting-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Data da reunião *">
                            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                                data-testid="complete-meeting-data" className="input" />
                        </Field>
                        <Field label="Tipo">
                            <input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                                data-testid="complete-meeting-tipo" className="input" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Participantes">
                            <input value={form.participantes} onChange={(e) => setForm({ ...form, participantes: e.target.value })}
                                placeholder="Nomes separados por vírgula" data-testid="complete-meeting-participantes" className="input" />
                        </Field>
                        <Field label="Clima da reunião">
                            <select value={form.clima} onChange={(e) => setForm({ ...form, clima: e.target.value })}
                                data-testid="complete-meeting-clima" className="input">
                                {Object.entries(CLIMA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Notas / resumo">
                        <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                            data-testid="complete-meeting-notas" className="input" />
                    </Field>
                    <Field label="Próximos passos (1. … 2. … — viram ações automáticas)">
                        <textarea rows={2} value={form.proximos_passos} onChange={(e) => setForm({ ...form, proximos_passos: e.target.value })}
                            data-testid="complete-meeting-proximos" className="input" />
                    </Field>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="complete-meeting-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="complete-meeting-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="complete-meeting-save"
                        className="text-sm px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4" /> {saving ? "Registrando…" : "Concluir e registrar reunião"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Concluir treinamento (jornada ou agenda do dashboard) → grava em Reuniões e Clima
export function CompleteTreinamentoTaskModal({ contract, task, onClose, onSaved }) {
    const [form, setForm] = useState({
        data: task.override_date || task.predicted_date || new Date().toISOString().slice(0, 10),
        duracao_minutos: 60,
        modulos: [],
        participantes: "",
        quantidade_participantes: 1,
        clima: "bom",
        notas: "",
        proximos_passos: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit() {
        if (!form.data) { setError("Informe a data do treinamento."); return; }
        if (form.modulos.length === 0) { setError("Selecione ao menos um módulo treinado."); return; }
        setSaving(true); setError("");
        try {
            await api.post(`/contracts/${contract.id}/treinamentos`, {
                data: form.data,
                modulos: form.modulos,
                duracao_minutos: Number(form.duracao_minutos),
                participantes: form.participantes,
                quantidade_participantes: Number(form.quantidade_participantes),
                clima: form.clima,
                notas: form.notas,
                proximos_passos: form.proximos_passos,
            });
            await api.patch(`/tasks/${task.id}`, {
                status: "concluido",
                observacao: `Treinamento realizado em ${form.data} — ${form.modulos.join(", ")}${form.notas ? ` — ${form.notas.slice(0, 120)}` : ""}`,
            });
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao registrar o treinamento.");
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 px-4 overflow-y-auto py-6" data-testid="complete-treino-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in my-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-semibold">Concluir treinamento</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{task.descricao}</h3>
                        <div className="text-xs text-slate-500 mt-1">{contract.organizacao_nome} · o registro entra na aba "Reuniões e Clima" (Treinamentos).</div>
                    </div>
                    <button onClick={onClose} data-testid="complete-treino-close" className="text-slate-400 hover:text-slate-700 -mt-1"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Data *">
                            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                                data-testid="complete-treino-data" className="input" />
                        </Field>
                        <Field label="Duração (min) *">
                            <input type="number" min={5} value={form.duracao_minutos} onChange={(e) => setForm({ ...form, duracao_minutos: e.target.value })}
                                data-testid="complete-treino-duracao" className="input" />
                        </Field>
                        <Field label="Qtd. particip.">
                            <input type="number" min={0} value={form.quantidade_participantes} onChange={(e) => setForm({ ...form, quantidade_participantes: e.target.value })}
                                data-testid="complete-treino-qtd" className="input" />
                        </Field>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Módulo(s) treinado(s) *</label>
                        <ModulosCheckboxes value={form.modulos} onChange={(m) => setForm({ ...form, modulos: m })} testidPrefix="complete-treino-modulo" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Participantes">
                            <input value={form.participantes} onChange={(e) => setForm({ ...form, participantes: e.target.value })}
                                placeholder="Nomes separados por vírgula" data-testid="complete-treino-participantes" className="input" />
                        </Field>
                        <Field label="Clima">
                            <select value={form.clima} onChange={(e) => setForm({ ...form, clima: e.target.value })}
                                data-testid="complete-treino-clima" className="input">
                                {Object.entries(CLIMA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Notas / resumo">
                        <textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                            data-testid="complete-treino-notas" className="input" />
                    </Field>
                    <Field label="Próximos passos">
                        <textarea rows={2} value={form.proximos_passos} onChange={(e) => setForm({ ...form, proximos_passos: e.target.value })}
                            data-testid="complete-treino-proximos" className="input" />
                    </Field>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="complete-treino-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="complete-treino-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="complete-treino-save"
                        className="text-sm px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4" /> {saving ? "Registrando…" : "Concluir e registrar treinamento"}
                    </button>
                </div>
            </div>
        </div>
    );
}
